import {
  applyFileSystemTreeDiff,
  mapUserMessageIdToFileSystemTreeIndex,
} from '@/lib/file-system-tree'
import type { FileSystemTreeDiff } from '@/lib/file-system-tree'
import type {
  BuilderTab,
  Project,
  ProjectMessage,
  Asset,
  Skill,
  User,
  InitialCodingStage,
  WorkspaceRole,
} from '@/lib/types'
import type { DirectoryNode, FileNode, FileSystemTree } from '@webcontainer/api'
import { last, omit } from 'lodash'
import { nanoid } from 'nanoid'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { STATUS_FILE_NAME, APPS_DIR_NAME } from '@/lib/constant'
import {
  type Feature,
  FEATURES,
} from '@/components/features/features-section/constants'
import {
  type Plugin,
  PLUGINS,
} from '@/components/features/plugins-section/constants'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { useAiCodingRetryStore } from '@/hooks/use-ai-coding-retry'
import { toast } from 'sonner'
import _ from 'lodash'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { CommonResp } from '@/backend/type'
import { ICustomCode } from '@/utils/seo'
import { usePreviewStore } from './use-preview-store'
import { MinimumRequiredVersion } from '@/lib/editor/version'

export type KnowledgeId = {
  ids: string[]
  collection: string
}

export type TProjectStateData = {
  project?: Project
  isOwner?: boolean
  workspaceRole?: WorkspaceRole | null
  isToolboxOpen: boolean
  selectedAssets: Asset[]
  elementSearchQuery: string
  isChatBarHighlighting: boolean
  prompt: string
  selectedTab: BuilderTab
  previousTab: BuilderTab
  isChatExpanded: boolean
  messagesSnapshot: ProjectMessage[] | null
  aiStage: 'idle' | 'coding' | 'building'
  aiCodingType: string
  aiMode: 'chat' | 'coding' | 'discuss' // Add discuss mode
  featuresToAdd: (Feature | Plugin)[]
  featuresAdded: (Feature | Plugin)[]
  featuresAndPlugins: (Feature | Plugin)[]
  isFixingIssue: boolean
  knowledge_ids: KnowledgeId[]
  pendingCredentialMessageId: string | null
  isCredentialCodingInFlight: boolean
  toastKeyInEditor: string
  pendingIntakePlanning: boolean
}

export type ProjectState = TProjectStateData & {
  setKnowledgeIds: (knowledgeIds: KnowledgeId[]) => void
  getKnowledgeIds: () => KnowledgeId[]
  setAiMode: (mode: 'chat' | 'coding' | 'discuss') => void // Add setter for aiMode
  initProjectStore: () => void
  getProject: () => Project | undefined
  setProject: (project: Project) => void
  setIsToolboxOpen: (isToolboxOpen: boolean) => void
  updateProject: (partialProject: Partial<Project>) => void
  checkEditorVersion: (iframeVersion: string) => void
  addMessage: (message: ProjectMessage) => void
  removeTemporaryMessages: () => void
  updateMessage: (
    messageId: string,
    updateFn: (message: ProjectMessage) => void,
  ) => void
  deleteMessage: (messageId: string) => void
  deleteRetryMessage: () => void
  deleteRecentCodingMessages: () => void
  getForkProjectId: () => string | null
  getForkProjectUser: () => User | null
  getForkProjectUserDisplayName: () => string | null
  getProjectId: () => string | null
  getLastMessage: (
    type: 'user' | 'assistant' | 'system',
  ) => ProjectMessage | null
  /**
   * Gets the latest user message to be sent to the AI
   * but filter the assets' icon payload
   */
  getMessagesToSentAI: () => ProjectMessage | null
  getFileSystemTree: () => FileSystemTree | null
  setFileSystemTreeListByIndex: (tree: FileSystemTree, index: number) => void
  getFileSystemTreeList: () => FileSystemTree[]
  getFileSystemTreeListInForkProject: () => FileSystemTree[]
  getFileSystemTreeListLength: () => number
  getFileSystemTreeListLengthInCurrentProject: () => number
  getFileSystemTreeListLengthInForkProject: () => number
  getFileSystemTreeByIndex: (index: number) => FileSystemTree | null
  setFileSystemTreeByIndex: (index: number) => void
  applyFileSystemTreeDiff: (diff: FileSystemTreeDiff) => void
  pushFileSystemTreeToList: (tree?: FileSystemTree) => void
  incFileSystemTreeIndex: () => void
  decFileSystemTreeIndex: () => void
  setFileSystemTreeIndex: (index: number) => void
  getFileSystemTreeIndex: () => number
  setFileSystemTreeIndexToLast: () => void
  getIsPromptContainsText: (text: string, ignoreCase?: boolean) => boolean
  getUserMessageIdToFileSystemTreeIndexInCurrentProject: () => Record<
    string,
    number
  >
  getUserMessageIdToFileSystemTreeIndexInForkProject: () => Record<
    string,
    number
  >
  getIsRemix: () => boolean
  getCanUndo: () => boolean
  getCanRedo: () => boolean
  getUserMessages: () => ProjectMessage[]
  getUserMessagesInForkProject: () => ProjectMessage[]
  getChatMessages: () => ProjectMessage[]
  getForkMessages: () => ProjectMessage[]
  getForkUserMessages: () => ProjectMessage[]
  getSelectedTab: () => BuilderTab
  setSelectedTab: (tab: BuilderTab) => void
  getPreviousTab: () => BuilderTab
  setChatExpanded: (expanded: boolean) => void
  setPendingCredentialMessageId: (messageId: string | null) => void
  getPendingCredentialMessageId: () => string | null
  setCredentialCodingInFlight: (inFlight: boolean) => void
  getCredentialCodingInFlight: () => boolean
  setPendingIntakePlanning: (pending: boolean) => void
  isUseTemplate: () => boolean
  isUserTemplateInForkProject: () => boolean
  isStartsWithUserPrompt: () => boolean
  isStartsWithUserPromptInForkProject: () => boolean
  isStartsWithUseTemplatePrompt: () => boolean
  updateProjectToDB: (options?: {
    append?: boolean
    except?: ('messages' | 'fileSystemTree')[]
  }) => Promise<void>
  setIsOwner: (isOwner: boolean) => void
  getIsOwner: () => boolean
  setWorkspaceRole: (role: WorkspaceRole | null) => void
  getWorkspaceRole: () => WorkspaceRole | null
  addSelectedAsset: (asset: Asset) => void
  removeSelectedAsset: (asset: Asset) => void
  clearSelectedAssets: () => void
  setElementSearchQuery: (query: string) => void
  setChatBarHighlighting: (isHighlighting: boolean) => void
  setPrompt: (prompt: string) => void
  appendPrompt: (prompt: string) => void
  saveMessagesSnapshot: () => void
  restoreMessagesSnapshot: () => void
  isFirstRound: () => boolean
  getAppsJSON: () => Record<string, any>
  updateAppsJSON: (appsJSON: Record<string, any>) => FileSystemTree | undefined
  getStatusJSON: () => {
    'step-1'?: 'completed' | 'not completed'
    'step-2'?: 'completed' | 'not completed'
    currentStep?: 1 | 2
  }
  deleteStatusJSON: () => void
  isStepOne: () => boolean
  isStepTwo: () => boolean
  setAiStage: (stage: 'idle' | 'coding' | 'building') => void
  getAiStage: () => 'idle' | 'coding' | 'building'
  setAiCodingType: (type: string) => void
  getAiCodingType: () => string | ''
  useSkill: (skill: Skill) => void
  refundCodingRequest: () => void
  hasForkFileSystemTreeListHistory: () => boolean
  getPlugins: () => Plugin[]
  getFeatures: () => Feature[]
  getFeaturesToAdd: () => (Feature | Plugin)[]
  hasFeatureToAdd: (feature: Feature | Plugin) => boolean
  addFeatureToAdd: (feature: Feature | Plugin) => void
  removeFeatureToAdd: (feature: Feature | Plugin) => void
  clearAllFeaturesToAdd: () => void
  canAddFeatureToAdd: () => boolean
  getFeaturesAndPlugins: () => (Feature | Plugin)[]
  setFeaturesAndPlugins: (featuresAndPlugins: (Feature | Plugin)[]) => void
  getInitialCodingStage: () => InitialCodingStage | undefined
  setInitialCodingStage: (stage: InitialCodingStage) => void
  checkoutVersion: (version?: number | string) => Promise<void>
  setIsFixingIssue: (isFixingIssue: boolean) => void
  isFirstTimeRelease: () => boolean
  updateProjectTableColumnsToDBAndSyncToStore: (payload: {
    action: 'add' | 'drop' | 'alter'
    columnName: string
    dataType?: string
    oldColumnName?: string
    oldDataType?: string
  }) => Promise<void>
  /**
   * POST /api/me/project/[projectId]/messages
   * Appends a message to the project
   *
   * @param message - The message to append
   * @returns The appended message with id
   */
  api__appendMessage: (
    message: Omit<ProjectMessage, 'id'> & { id?: string },
    overwrite?: boolean,
  ) => Promise<ProjectMessage | null>
  setCustomCode: (customCode: ICustomCode) => void
}

const initialState = {
  selectedAssets: [],
  elementSearchQuery: '',
  isToolboxOpen: false,
  isChatBarHighlighting: false,
  prompt: '',
  selectedTab: '' as const,
  previousTab: 'design' as const,
  isChatExpanded: true,
  messagesSnapshot: null,
  aiStage: 'idle' as const,
  aiCodingType: '' as string,
  aiMode: 'chat' as const,
  featuresToAdd: [],
  featuresAdded: [],
  featuresAndPlugins: [...FEATURES, ...PLUGINS],
  initialCodingStage: 'ing' as const,
  isFixingIssue: false,
  knowledge_ids: [],
  pendingCredentialMessageId: null,
  isCredentialCodingInFlight: false,
  toastKeyInEditor: '',
  pendingIntakePlanning: false,
}

export const useProjectStore = create<ProjectState>()(
  immer<ProjectState>((set, get) => ({
    ...initialState,
    initProjectStore: () => set(initialState),
    getProject: () => get().project,
    setProject: (project: Project) => set({ project }),

    checkoutVersion: async (version?: number | string) => {
      const projectInState = get().project
      if (!projectInState) {
        console.log('[checkoutVersion] No project found, skipping checkout')
        return
      }

      const projectId = get().getProjectId()
      if (!projectId) {
        console.log('[checkoutVersion] No projectId found, skipping checkout')
        return
      }

      console.log('[checkoutVersion] Starting checkout:', {
        projectId,
        requestedVersion: version,
        currentIndex: projectInState.file_system_tree_index,
        currentVersionCount: Object.keys(projectInState.versions || {}).length,
      })

      try {
        const response = await fetch(`/api/projects/${projectId}/checkout`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            version,
          }),
        })
        const resp: CommonResp<Project> = await response.json()
        if (!resp.success || !resp.data) {
          throw new Error(resp.message || 'Failed to checkout version')
        }
        const project = resp.data

        console.log('[checkoutVersion] Received updated project:', {
          newIndex: project?.file_system_tree_index,
          newVersionCount: Object.keys(project?.versions || {}).length,
          hasFileSystemTree: !!project?.file_system_tree,
          fileSystemTreeListLength: project?.file_system_tree_list?.length,
        })

        // Update project with the returned data
        set((state) => {
          console.log('[checkoutVersion] Updating project in store')
          state.project = project
        })
        window.postMessage({ type: 'checkout-version-success' }, '*')
      } catch (error: any) {
        console.error('[checkoutVersion] Error checking out version:', error)
        toast.error(error?.message || 'Failed to checkout version')
      }
    },
    setIsToolboxOpen: (isToolboxOpen) => {
      set({ isToolboxOpen })
      if (!isToolboxOpen) {
        set({ selectedTab: '' })
      }
    },
    setKnowledgeIds: (knowledgeIds: KnowledgeId[]) =>
      set({ knowledge_ids: knowledgeIds }),
    getKnowledgeIds: () => get().knowledge_ids,
    updateProject: (partialProject: Partial<Project>) =>
      set((state) => {
        if (!state.project) return
        Object.assign(state.project, partialProject)
      }),
    setIsOwner: (isOwner: boolean) => set({ isOwner }),
    getIsOwner: () => !!get().isOwner,
    setWorkspaceRole: (role: WorkspaceRole | null) =>
      set({ workspaceRole: role }),
    getWorkspaceRole: () => get().workspaceRole ?? null,
    addMessage: (message) =>
      set((state) => {
        if (!state.project) return
        if (state.project.messages.find((m) => m.id === message.id)) {
          return
        }
        state.project.messages.push({
          ...message,
          // Ensure timestamp is always set
          timestamp: message.timestamp ?? Date.now(),
          // message will be isTemporary if it's not set which means it is added from frontend
          ...((message.isTemporary || _.isUndefined(message.isTemporary)) && {
            isTemporary: true,
          }),
        })
      }),
    removeTemporaryMessages: () =>
      set((state) => {
        if (!state.project) return
        state.project.messages = state.project.messages.filter(
          (message) => !message.isTemporary,
        )
      }),
    updateMessage: (
      messageId: string,
      updateFn: (message: ProjectMessage) => void,
    ) =>
      set((state) => {
        if (!state.project) return
        const message = state.project.messages.find(
          (message) => message.id === messageId,
        )
        if (message) {
          updateFn(message)
        }
      }),
    deleteMessage: (messageId: string) =>
      set((state) => {
        if (!state.project) return
        state.project.messages = state.project.messages.filter(
          (message) => message.id !== messageId,
        )
      }),
    deleteRetryMessage: () =>
      set((state) => {
        if (!state.project) return
        state.project.messages = state.project.messages.filter(
          (message) => message.type !== 'retry',
        )
      }),
    deleteRecentCodingMessages: () =>
      set((state) => {
        if (!state.project) return
        const lastUserMessageIdx = state.project.messages.findLastIndex(
          (message) => message.role === 'user',
        )
        if (lastUserMessageIdx === -1) return
        state.project.messages = state.project.messages.slice(
          0,
          lastUserMessageIdx + 1,
        )
      }),
    applyFileSystemTreeDiff: (diff: FileSystemTreeDiff) =>
      set((state) => {
        if (!state.project) return
        const newFileSystemTree = applyFileSystemTreeDiff(
          state.project.file_system_tree,
          diff,
        )
        state.project.file_system_tree = newFileSystemTree
      }),
    pushFileSystemTreeToList: (tree?: FileSystemTree) =>
      set((state) => {
        if (!state.project) return
        state.project.file_system_tree_list.push(
          tree || state.project.file_system_tree,
        )
      }),
    incFileSystemTreeIndex: () =>
      set((state) => {
        if (!state.project) return
        if (
          state.project.file_system_tree_index >=
          state.project.file_system_tree_list.length - 1
        )
          return
        state.project.file_system_tree_index += 1
      }),
    decFileSystemTreeIndex: () =>
      set((state) => {
        if (!state.project) return
        if (state.project.file_system_tree_index <= 0) return
        state.project.file_system_tree_index -= 1
      }),
    setFileSystemTreeIndex: (index: number) =>
      set((state) => {
        if (!state.project) return
        if (index < 0 || index >= state.project.file_system_tree_list.length)
          return
        state.project.file_system_tree_index = index
      }),
    setFileSystemTreeIndexToLast: () =>
      set((state) => {
        if (!state.project) return
        state.project.file_system_tree_index =
          state.project.file_system_tree_list.length - 1
      }),
    getFileSystemTreeByIndex: (index: number) => {
      const fileSystemTreeList = get().getFileSystemTreeList()
      if (index < 0 || index >= fileSystemTreeList.length) return null
      return fileSystemTreeList[index]
    },
    setFileSystemTreeByIndex: (index: number) =>
      set((state) => {
        if (!state.project) return
        if (index < 0 || index >= state.project.file_system_tree_list.length)
          return
        state.project.file_system_tree =
          state.project.file_system_tree_list[index]
      }),
    getIsPromptContainsText: (text: string, ignoreCase = true) => {
      const prompt = get().prompt
      if (ignoreCase) {
        return prompt.toLowerCase().includes(text.toLowerCase())
      }
      return prompt.includes(text)
    },
    getIsRemix: () => {
      const project = get().project
      if (!project) return false
      return Boolean(
        project.fork_time && project.fork_file_system_tree_list_length === 0,
      )
    },
    getCanUndo: () => {
      return Boolean(
        get().isOwner &&
          get().getFileSystemTreeList().length > 1 &&
          get().getFileSystemTreeIndex() > 0 &&
          get().aiStage !== 'coding',
      )
    },
    getCanRedo: () => {
      const length = get().getFileSystemTreeList().length
      return Boolean(
        get().isOwner &&
          length > 1 &&
          get().getFileSystemTreeIndex() < length - 1 &&
          get().aiStage !== 'coding',
      )
    },
    getFileSystemTree: () => {
      const project = get().project
      if (!project) return null
      return (
        project.file_system_tree_list[project.file_system_tree_index ?? 0] ||
        project.file_system_tree
      )
    },
    setFileSystemTreeListByIndex: (tree: FileSystemTree, index: number) => {
      set((state) => {
        if (!state.project) return
        const newFileSystemTreeList = state.project.file_system_tree_list
        newFileSystemTreeList[index] = tree
        state.project.file_system_tree_list = newFileSystemTreeList
      })
    },
    /**
     * 获取原始项目的 fileSystemTreeList (获取 fork 项目的 fileSystemTreeList 包含在里面)
     */
    getFileSystemTreeList: () => {
      return get().project?.file_system_tree_list || []
    },
    /**
     * 获取 fork 项目的 fileSystemTreeList
     */
    getFileSystemTreeListInForkProject: () => {
      const project = get().project
      if (!project) return []
      const forkFileSystemTreeListLength =
        project.fork_file_system_tree_list_length || 0
      return (
        project?.file_system_tree_list.slice(0, forkFileSystemTreeListLength) ||
        []
      )
    },
    getFileSystemTreeIndex: () => {
      return get().project?.file_system_tree_index || 0
    },
    /**
     * getFileSystemTreeListLengthInCurrentProject 获取原始项目的 fileSystemTreeList 长度
     * getFileSystemTreeListLengthInForkProject 获取 fork 项目的 fileSystemTreeList 长度
     * @description 请区分场景使用
     */
    getFileSystemTreeListLength: () => {
      return get().getFileSystemTreeList().length
    },
    getFileSystemTreeListLengthInCurrentProject: () => {
      const totalLength = get().getFileSystemTreeList().length
      const forkFileSystemTreeListLength =
        get().getFileSystemTreeListLengthInForkProject()
      return totalLength - forkFileSystemTreeListLength
    },
    getFileSystemTreeListLengthInForkProject: () => {
      return get().getFileSystemTreeListInForkProject().length
    },
    getUserMessageIdToFileSystemTreeIndexInCurrentProject: () => {
      const state = get()
      const remix = state.getIsRemix()
      const messages = state.project?.messages || []
      const offset = state.getFileSystemTreeListLengthInForkProject()
      const treelist = state.getFileSystemTreeList().slice(offset)
      return mapUserMessageIdToFileSystemTreeIndex(
        messages,
        treelist,
        offset,
        remix,
      )
    },
    getUserMessageIdToFileSystemTreeIndexInForkProject: () => {
      const state = get()
      const messages = state.project?.fork_messages || []
      const treelist = state.getFileSystemTreeListInForkProject()
      return mapUserMessageIdToFileSystemTreeIndex(messages, treelist)
    },
    getForkProjectId: () => {
      const project = get().project
      if (!project) return null
      return project.fork_project_id || null
    },
    getForkProjectUser: () => {
      const project = get().project
      if (!project) return null
      return project.fork_project_user || null
    },
    getForkProjectUserDisplayName: () => {
      const user = get().getForkProjectUser()
      if (!user) return null
      return user.name || user.username || user.email.split('@')[0] || ''
    },
    getProjectId: () => {
      const project = get().project
      if (!project) return null
      return project._id.toString()
    },
    getLastMessage: (type: 'user' | 'assistant' | 'system') => {
      const project = get().project
      if (!project) return null
      return last(
        project.messages.filter((message) => message.role === type),
      ) as ProjectMessage | null
    },
    getMessagesToSentAI: () => {
      const project = get().project
      if (!project) return null

      const lastUserMessage = last(
        project.messages.filter((message) => message.role === 'user'),
      ) as ProjectMessage

      return {
        ...lastUserMessage,
        assets: lastUserMessage.assets
          ? lastUserMessage.assets.map((d) => {
              if (d.type === 'icon') return omit(d, ['payload'])
              return d
            })
          : undefined,
      }
    },
    getUserMessages: () => {
      const project = get().project
      if (!project) return []
      return (project.messages ?? []).filter(
        (message) => message.role === 'user' && !message.hidden,
      )
    },
    getUserMessagesInForkProject: () => {
      const project = get().project
      if (!project) return []
      return (
        project.fork_messages?.filter(
          (msg) => msg.role === 'user' && !msg.hidden,
        ) || []
      )
    },
    getChatMessages: () => {
      const project = get().project
      const aiStage = get().getAiStage()
      if (!project) return []

      // removed filter
      const filteredMessages = project.messages

      // Find all user message indices
      const userMessageIndices: number[] = []
      filteredMessages.forEach((message, index) => {
        if (message.role === 'user') {
          userMessageIndices.push(index)
        }
      })

      // Build result array with inserted preview_complete messages
      const result: ProjectMessage[] = []

      for (let i = 0; i < filteredMessages.length; i++) {
        const currentMessage = filteredMessages[i]
        result.push(currentMessage)
      }

      // Ensure only one initial_coding_done message exists (keep the first one)
      let firstInitialCodingDoneIndex = -1
      const indicesToRemove: number[] = []

      result.forEach((message, index) => {
        if (message.type === 'initial_coding_done') {
          if (firstInitialCodingDoneIndex === -1) {
            firstInitialCodingDoneIndex = index
          } else {
            indicesToRemove.push(index)
          }
        }
      })

      // Remove duplicate initial_coding_done messages (keep only the first one)
      const filteredResult = result.filter(
        (_, index) => !indicesToRemove.includes(index),
      )

      // Only keep the LAST retry message (represents final failure state)
      // Find all retry message indices
      const retryMessageIndices: number[] = []
      filteredResult.forEach((message, index) => {
        if (message.type === 'retry') {
          retryMessageIndices.push(index)
        }
      })

      // If multiple retry messages exist, only keep the last one
      const retryIndicesToRemove =
        retryMessageIndices.length > 1
          ? retryMessageIndices.slice(0, -1) // Remove all except the last one
          : []

      const finalResult = filteredResult.filter(
        (_, index) => !retryIndicesToRemove.includes(index),
      )

      return finalResult
    },
    getForkMessages: () => {
      const project = get().project
      if (!project) return []
      return project.fork_messages || []
    },
    getForkUserMessages: () => {
      const project = get().project
      if (!project) return []
      return (
        project.fork_messages?.filter(
          (msg) => msg.role === 'user' && !msg.hidden,
        ) || []
      )
    },
    getSelectedTab: () => {
      return get().selectedTab
    },
    setSelectedTab: (tab: BuilderTab) =>
      set((state) => {
        state.previousTab = state.selectedTab
        state.selectedTab = tab
      }),
    getPreviousTab: () => {
      return get().previousTab
    },
    setChatExpanded: (expanded: boolean) =>
      set((state) => {
        state.isChatExpanded = expanded
      }),
    setPendingCredentialMessageId: (messageId: string | null) =>
      set({ pendingCredentialMessageId: messageId }),
    getPendingCredentialMessageId: () => {
      return get().pendingCredentialMessageId
    },
    setCredentialCodingInFlight: (inFlight: boolean) =>
      set({ isCredentialCodingInFlight: inFlight }),
    getCredentialCodingInFlight: () => {
      return get().isCredentialCodingInFlight
    },
    setPendingIntakePlanning: (pending: boolean) =>
      set({ pendingIntakePlanning: pending }),
    isUseTemplate: () => {
      const messages = get().getChatMessages()
      return messages
        .slice(0, 2)
        .some((msg) => msg.content?.startsWith('Use template'))
    },
    isUserTemplateInForkProject: () => {
      const messages = get().getUserMessagesInForkProject()
      return messages
        .slice(0, 2)
        .some((msg) => msg.content?.startsWith('Use template'))
    },
    isStartsWithUserPrompt: () => {
      const messages = get().getChatMessages()
      return Boolean(messages[0]?.role === 'user')
    },
    isStartsWithUserPromptInForkProject: () => {
      const messages = get().getUserMessagesInForkProject()
      return Boolean(messages[0]?.role === 'user')
    },
    isStartsWithUseTemplatePrompt: () => {
      const messages = get().getChatMessages()
      return Boolean(
        messages[0]?.role === 'assistant' &&
          messages[0]?.content?.startsWith('Use template'),
      )
    },
    // todo: moving update messages to backend
    updateProjectToDB: async ({
      append = false,
      except = ['fileSystemTree', 'fileSystemTreeIndex'],
    }: {
      append?: boolean
      except?: ('messages' | 'fileSystemTree' | 'fileSystemTreeIndex')[]
    } = {}) => {
      const project = get().project
      if (!project) return
      const messages = get().getChatMessages()
      const fileSystemTree = get().getFileSystemTree()
      const fileSystemTreeIndex = get().getFileSystemTreeIndex()
      const formData = new FormData()
      // if (append) {
      //   formData.append('__append', 'true')
      // }
      if (!except.includes('messages')) {
        formData.append('messages', JSON.stringify(messages))
      }
      if (!except.includes('fileSystemTree')) {
        formData.append('fileSystemTree', JSON.stringify(fileSystemTree))
      }
      formData.append('projectId', project._id.toString())
      if (!except.includes('fileSystemTreeIndex')) {
        formData.append('fileSystemTreeIndex', fileSystemTreeIndex.toString())
      }
      formData.append('isInit', 'true')

      const projectRes = await fetch('/api/me/project', {
        method: 'POST',
        body: formData,
      })
      const projectData = await projectRes.json()
      if (projectRes.status === 200 && projectData.project) {
        set({ project: projectData.project as Project })
      }
    },
    updateProjectTableColumnsToDBAndSyncToStore: async ({
      action,
      columnName,
      dataType,
      oldColumnName,
      oldDataType,
    }: {
      action: 'add' | 'drop' | 'alter'
      columnName: string
      dataType?: string
      oldColumnName?: string
      oldDataType?: string
    }) => {
      const project = get().project
      if (!project) return
      // Allowed types for TableFieldDefinition
      const allowedTypes = [
        'id',
        'number',
        'timestamp',
        'media_url',
        'file_url',
        'text',
        'rich_text',
        'checkbox',
        'tag',
        'web_url',
        'address',
      ] as const
      // Map dataType to allowed type, fallback to 'text'
      const mapType = (type: string | undefined) =>
        allowedTypes.includes(type as any) ? type : 'text'
      // Clone table_columns
      const tableColumns = _.cloneDeep(project.table_columns) || []
      // Assume only one table, or use the first one
      if (tableColumns.length === 0) return
      const table = tableColumns[0]
      table.field_list = table.field_list || []
      if (action === 'add') {
        // Add new column if not exists
        if (!table.field_list.find((col: any) => col.db_name === columnName)) {
          const type = mapType(dataType) as any
          if (type === 'tag') {
            table.field_list.push({
              db_name: columnName,
              ui_name: columnName,
              type: 'tag',
              required: false,
              tag_values: [],
              tag_type: 'single',
            })
          } else {
            table.field_list.push({
              db_name: columnName,
              ui_name: columnName,
              type,
              required: false,
            })
          }
        }
      } else if (action === 'drop') {
        // Remove column
        table.field_list = table.field_list.filter(
          (col: any) => col.db_name !== columnName,
        )
      } else if (action === 'alter') {
        if (oldColumnName !== columnName && oldDataType === dataType) {
          table.field_list = table.field_list.map((col: any) => {
            if (col.db_name === oldColumnName) {
              return { ...col, db_name: columnName, ui_name: columnName }
            }
            return col
          })
        } else if (oldColumnName === columnName && oldDataType !== dataType) {
          table.field_list = table.field_list.map((col: any) => {
            if (col.db_name === oldColumnName) {
              return { ...col, type: dataType }
            }
            return col
          })
        } else if (oldColumnName !== columnName && oldDataType !== dataType) {
          table.field_list = table.field_list.map((col: any) => {
            if (col.db_name === oldColumnName) {
              return {
                ...col,
                db_name: columnName,
                ui_name: columnName,
                type: dataType,
              }
            }
            return col
          })
        } else {
          // No changes detected do nothing
        }
      }
      const formData = new FormData()
      formData.append('projectId', project._id.toString())
      formData.append('tableColumns', JSON.stringify(tableColumns))
      const projectRes = await fetch('/api/me/project', {
        method: 'POST',
        body: formData,
      })
      const projectData = await projectRes.json()
      if (projectRes.status === 200 && projectData.project) {
        set({ project: projectData.project as Project })
      }
    },
    addSelectedAsset: (asset: Asset) =>
      set((state) => {
        state.selectedAssets.push(asset)
        // if (!state.prompt.includes(ASSETS_PROMPT)) {
        //   state.prompt = `${state.prompt}${state.prompt ? '\n' : ''}${ASSETS_PROMPT}`
        // }
      }),
    removeSelectedAsset: (asset: Asset) =>
      set((state) => {
        state.selectedAssets = state.selectedAssets.filter(
          (a) => !(a.type === asset.type && a.link === asset.link),
        )
        // if (state.selectedAssets.length === 0) {
        //   state.prompt = state.prompt.replace(ASSETS_PROMPT, '')
        // }
      }),
    clearSelectedAssets: () =>
      set((state) => {
        state.selectedAssets = []
        // state.prompt = state.prompt.replace(ASSETS_PROMPT, '')
      }),
    setElementSearchQuery: (query: string) =>
      set((state) => {
        state.elementSearchQuery = query
      }),
    setChatBarHighlighting: (isHighlighting: boolean) =>
      set((state) => {
        state.isChatBarHighlighting = isHighlighting
      }),
    setPrompt: (prompt: string) =>
      set((state) => {
        state.prompt = prompt
      }),
    appendPrompt: (prompt: string) =>
      set((state) => {
        state.prompt = `${state.prompt}${state.prompt ? ' ' : ''}${prompt}`
      }),
    saveMessagesSnapshot: () =>
      set((state) => {
        if (!state.project) return
        const latestNotUserMessageIdx = state.project.messages.findLastIndex(
          (msg) => msg.role !== 'user',
        )
        state.messagesSnapshot = [...state.project.messages].slice(
          0,
          latestNotUserMessageIdx + 1,
        )
      }),
    restoreMessagesSnapshot: () => {
      set((state) => {
        if (!state.project || !state.messagesSnapshot) return

        // TODO: restore prompt and selected assets
        // const lastUserMessage = state.project.messages.findLast(
        //   (msg) => msg.role === 'user',
        // )
        // if (lastUserMessage) {
        //   state.prompt = lastUserMessage.content || ''
        //   state.selectedAssets = lastUserMessage.images || []
        // }

        state.project.messages = state.messagesSnapshot
        state.messagesSnapshot = null
      })
    },
    isFirstRound: () => {
      const messages = get().getChatMessages()
      const previewCompleteMessage = messages.find(
        (msg) => msg.type === 'preview_complete',
      )
      return !previewCompleteMessage
    },
    /**
     * apps 的 json 文件，都放在 Features/[category].[feature_name].[index].json 文件中
     * @returns {
     *  [category].[feature_name].[index].json: {
     *    url: 'xxx'
     *  }
     * }
     */
    getAppsJSON: () => {
      const project = get().project

      const targetDir = (project?.file_system_tree?.[APPS_DIR_NAME] ||
        project?.file_system_tree?.[`src/${APPS_DIR_NAME}`]) as DirectoryNode

      if (!targetDir) {
        return {}
      }

      const result: Record<string, any> = {}

      for (const [filename, dir] of Object.entries(targetDir.directory)) {
        try {
          result[filename] = JSON.parse(
            ((dir as FileNode)?.file?.contents as string) || '{}',
          )
        } catch (e) {
          result[filename] = {}
        }
      }

      return result
    },
    updateAppsJSON: (appsJSON: Record<string, any>) => {
      const { project } = get()

      if (!project) return

      /**
       * {directory: {
       *  [category].[feature_name].[index].json: {
       *    url: 'xxx'
       *  }
       * }}
       */
      const targetDir = (project?.file_system_tree?.[APPS_DIR_NAME] ||
        project?.file_system_tree?.[`src/${APPS_DIR_NAME}`]) as DirectoryNode
      if (!targetDir) return

      // 要改动的文件
      const diffs: FileSystemTreeDiff = []
      for (const [filename, contents] of Object.entries(appsJSON)) {
        diffs.push({
          type: 'update',
          path: `${APPS_DIR_NAME}/${filename}`,
          contents: JSON.stringify(contents),
        })
      }

      const newFileSystemTree = applyFileSystemTreeDiff(
        project.file_system_tree,
        diffs,
      )

      return newFileSystemTree
    },
    getStatusJSON: () => {
      const project = get().project
      const file = (project?.file_system_tree?.[STATUS_FILE_NAME] ||
        project?.file_system_tree?.[`src/${STATUS_FILE_NAME}`]) as FileNode
      if (!file?.file?.contents) {
        return {}
      }
      try {
        return JSON.parse(file.file.contents as string)
      } catch (e) {
        return {}
      }
    },
    deleteStatusJSON: () => {
      const { applyFileSystemTreeDiff } = get()

      applyFileSystemTreeDiff([
        {
          type: 'remove',
          path: STATUS_FILE_NAME,
        },
        {
          type: 'remove',
          path: `src/${STATUS_FILE_NAME}`,
        },
      ])
    },
    isStepOne: () => {
      const statusJSON = get().getStatusJSON()

      if (typeof statusJSON?.currentStep === 'number') {
        return statusJSON?.currentStep === 1
      }

      if (statusJSON?.['step-1'] === 'completed') {
        return true
      }

      if (statusJSON?.['step-1'] === 'not completed') {
        return true
      }

      if (statusJSON?.['step-2'] === 'not completed') {
        return true
      }

      return false
    },
    isStepTwo: () => {
      const statusJSON = get().getStatusJSON()

      if (typeof statusJSON?.currentStep === 'number') {
        return statusJSON?.currentStep === 2
      }

      if (statusJSON?.['step-2'] === 'completed') {
        return true
      }

      return false
    },
    setAiStage: async (stage: 'idle' | 'coding' | 'building') => {
      const project = get().project
      if (!project) return
      const formData = new FormData()
      formData.append('projectId', project._id.toString())
      formData.append('aiStage', stage)
      const projectRes = await fetch('/api/me/project', {
        method: 'POST',
        body: formData,
      })
      if (projectRes.status === 200) {
        set((state) => {
          state.aiStage = stage
          console.log('setAiStage', stage)
        })
      }
    },
    getAiStage: () => get().aiStage || 'idle',
    setAiCodingType: (type: string) =>
      set((state) => {
        state.aiCodingType = type
        console.log('setAiCodingType', type)
      }),
    getAiCodingType: () => get().aiCodingType || '',
    useSkill: (skill: Skill) => {
      const actions = skill.actions
      for (const action of actions) {
        if (action.type === 'prompt') {
          get().appendPrompt(action.value)
        } else if (action.type === 'asset') {
          get().addSelectedAsset({
            source: 'elements',
            type: 'image',
            link: action.value,
            thumbnail: action.value,
          })
        }
      }
      sendToBothAnalytics(MIXPANEL_CLIENT_EVENT_NAMES.USE_SKILL, {
        skill: skill.title,
      })
    },
    refundCodingRequest: () => {
      try {
        const project = get().project
        if (!project) return
        const currentFileSystemTreeIndex = project.file_system_tree_index
        // h/t: file_system_tree_index: 0 = default, 1 = the first new tree generated by ai coding
        const currentUserMessageIndex = currentFileSystemTreeIndex - 1
        if (currentUserMessageIndex < 0) return
        const userMessages = get().getUserMessages()
        const currentUserMessage = userMessages[currentUserMessageIndex]
        if (!currentUserMessage) return
        const messageId = currentUserMessage.id
        if (!messageId) return
        console.log('refundCodingRequest:', messageId, currentUserMessage)
        fetch('/api/coding/refund', {
          method: 'POST',
          body: JSON.stringify({ messageId }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (
              data.totalDeductCoins > 0 &&
              !useAiCodingRetryStore.getState().retryIng
            ) {
              get().addMessage({
                id: nanoid(),
                role: 'assistant',
                content:
                  '🚨 **AI had a bug. We have refunded you the tokens for this round.**',
              })
            }
          })
          .catch((e) => {
            console.error('refundCodingRequest:', e)
          })
      } catch (e) {
        console.error('refundCodingRequest:', e)
      }
    },
    hasForkFileSystemTreeListHistory: () => {
      const project = get().project
      return (
        typeof project?.fork_file_system_tree_list_length === 'number' &&
        project?.fork_file_system_tree_list_length > 0
      )
    },
    getFeaturesToAdd: () => {
      return get().featuresToAdd
    },
    hasFeatureToAdd: (feature: Feature | Plugin) =>
      get().featuresToAdd.some((f) => f.id === feature.id),
    addFeatureToAdd: (feature: Feature | Plugin) =>
      set((state) => {
        if (state.featuresToAdd.length >= 3) return
        if (state.hasFeatureToAdd(feature)) return
        state.featuresToAdd.push(feature)
        state.prompt = `${state.prompt}${state.prompt ? '\n' : ''}* ${feature.title}: ${feature.prompt}`
      }),
    removeFeatureToAdd: (feature: Feature | Plugin) =>
      set((state) => {
        state.featuresToAdd = state.featuresToAdd.filter(
          (f) => f.id !== feature.id,
        )

        // featuer.title === "MythoMax (via Openrouter)"的时候，为什么 replace 不生效？
        //  state.prompt = state.prompt.replace(
        //   new RegExp(
        //     `\\* ${feature.title}: ${feature.prompt.slice(0, 20)}.*\\n?`,
        //   ),
        //   '',
        // )

        // 构建一个更安全的正则表达式模式
        const featurePattern = `\\* ${_.escapeRegExp(feature.title)}: ${_.escapeRegExp(feature.prompt)}\\n?`
        state.prompt = state.prompt.replace(new RegExp(featurePattern, 'g'), '')

        // 清理可能留下的空行
        state.prompt = state.prompt.trim()
      }),
    clearAllFeaturesToAdd: () =>
      set((state) => {
        state.featuresToAdd = []
      }),
    canAddFeatureToAdd: () => {
      return get().featuresToAdd.length < 3
    },
    getFeaturesAndPlugins: () => {
      return get().featuresAndPlugins
    },
    setFeaturesAndPlugins: (featuresAndPlugins: (Feature | Plugin)[]) =>
      set((state) => {
        state.featuresAndPlugins = featuresAndPlugins
      }),
    getFeatures: () => {
      return get().featuresAndPlugins.filter((f) => f.type === 'feature')
    },
    getPlugins: () => {
      return get().featuresAndPlugins.filter(
        (f) => f.type === 'plugin',
      ) as Plugin[]
    },
    getInitialCodingStage: () => {
      return get().project?.initial_coding_stage
    },
    setInitialCodingStage: async (stage: InitialCodingStage) => {
      set((state) => {
        if (!state.project) return
        state.project.initial_coding_stage = stage
      })

      const project = get().project
      if (!project) return
      const formData = new FormData()
      formData.append('projectId', project._id.toString())
      formData.append('initialCodingStage', stage)
      await fetch('/api/me/project', { method: 'POST', body: formData })
    },
    setIsFixingIssue: (isFixingIssue: boolean) =>
      set((state) => {
        state.isFixingIssue = isFixingIssue
      }),
    /**
     * 判断是否是第一次发布，第一次发布没有 deploy_url
     */
    isFirstTimeRelease: () => {
      const project = get().project
      return !project?.deploy_url
    },
    setAiMode: (mode: 'chat' | 'coding' | 'discuss') =>
      set((state) => {
        state.aiMode = mode
      }),

    checkEditorVersion: async (iframeVersion: string) => {
      console.log(
        '🟠 [PROJECT] Checking editor version:',
        iframeVersion,
        'vs required:',
        MinimumRequiredVersion,
      )

      // Simple version comparison (works for semantic versions like 1.3.1 vs 1.3.2)
      const compareVersions = (v1: string, v2: string) => {
        const parts1 = v1.split('.').map(Number)
        const parts2 = v2.split('.').map(Number)
        for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
          const p1 = parts1[i] || 0
          const p2 = parts2[i] || 0
          if (p1 < p2) return -1
          if (p1 > p2) return 1
        }
        return 0
      }

      if (compareVersions(iframeVersion, MinimumRequiredVersion) < 0) {
        console.log(
          '🟠 [PROJECT] Editor version is outdated, triggering rebuild...',
        )
        const projectId = get().getProjectId()
        if (!projectId) return

        // Set preview stage to 'starting' to show loading UI
        usePreviewStore.getState().setStage('starting')

        set((state) => {
          state.toastKeyInEditor = 'rebuild_starting'
        })

        try {
          // Trigger a rebuild to update the editor script
          const response = await fetch(`/api/me/project/${projectId}/editor`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileSystemTree: get().getFileSystemTree(),
              forceRebuild: true,
            }),
          })

          if (response.ok) {
            console.log('🟠 [PROJECT] Editor updated successfully')
            set((state) => {
              state.toastKeyInEditor = 'rebuild_success'
            }) // Reload just the iframe, not the whole page
            usePreviewStore.getState().reloadPreviewPage()
          } else {
            console.error(
              '🟠 [PROJECT] Failed to update editor:',
              response.statusText,
            )
            set((state) => {
              state.toastKeyInEditor = 'rebuild_failed'
            })
            // Reset stage on error
            usePreviewStore.getState().setStage('error')
          }
        } catch (error) {
          console.error('🟠 [PROJECT] Error updating editor:', error)
          set((state) => {
            state.toastKeyInEditor = 'rebuild_failed'
          })
          // Reset stage on error
          usePreviewStore.getState().setStage('error')
        }
      } else {
        console.log('🟠 [PROJECT] Editor version is up to date')
      }
    },
    api__appendMessage: async (
      message: Omit<ProjectMessage, 'id'> & { id?: string },
      overwrite = false,
    ) => {
      try {
        const project = get().project
        if (!project) return null

        const response = await fetch(`/api/me/project/${project._id}/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message, overwrite }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          console.error('Failed to append message:', {
            status: response.status,
            error: errorData,
            projectId: project._id,
          })
          throw new Error(
            `Failed to append message: ${errorData.error || response.statusText}`,
          )
        }

        const result = (await response.json()) as {
          success: boolean
          message: ProjectMessage
          duplicate: boolean
        }
        return result.message
      } catch (error) {
        console.error('api__appendMessage:', error)
        return null
      }
    },
    setCustomCode: (customCode: ICustomCode) =>
      set((state) => {
        if (!state.project) return
        state.project.customCode = {
          ...state.project.customCode,
          ...customCode,
        }
      }),
  })),
)
